"use client";

import { Blocks, Globe2, ShieldCheck } from "lucide-react";
import Link from "next/link";
import { PopText, Reveal } from "@/components/motion/reveal";
import { FeatureList } from "@/components/landing/beats/feature-list";
import { WalletAdapter } from "@/components/landing/wallet-adapter";
import { Button } from "@/components/ui/button";
import { GradientText } from "@/components/ui/gradient-text";
import { Spotlight } from "@/components/ui/spotlight";
import { CONTACT_URL } from "@/lib/links";

const FEATURES = [
  { icon: Blocks, text: "Drop-in provider + hooks — live in minutes" },
  { icon: ShieldCheck, text: "Instant wallets — no seed phrases" },
  { icon: Globe2, text: "Every chain, fiat in and out, built in" },
];

function CodeBlock() {
  return (
    <div className="overflow-hidden rounded-2xl border border-border/60 bg-card/70 backdrop-blur-xl shadow-[0_8px_40px_-16px_rgba(0,0,0,0.5)]">
      <div className="flex items-center gap-2 border-b border-border/60 px-4 py-2.5">
        <span className="size-3 rounded-full bg-rose-400/70" />
        <span className="size-3 rounded-full bg-amber-400/70" />
        <span className="size-3 rounded-full bg-emerald-400/70" />
        <span className="ml-2 text-xs text-muted-foreground">App.tsx</span>
      </div>
      <pre className="overflow-x-auto p-5 text-sm leading-relaxed">
        <code className="font-mono">
          <span className="text-primary">import</span>
          <span className="text-foreground/80">{" { MoonGateProvider, useWallet } "}</span>
          <span className="text-primary">from</span>
          <span className="text-emerald-400">{" '@moongate/sdk'"}</span>
          <span className="text-foreground/60">;</span>
          {"\n\n"}
          <span className="text-primary">const</span>
          <span className="text-foreground">{" { publicKey, sendTransaction, signTransaction, signMessage, connected } "}</span>
          <span className="text-foreground/60">=</span>
          <span className="text-sky-400">{" useWallet"}</span>
          <span className="text-foreground/60">();</span>
          {"\n\n"}
          <span className="text-primary">return</span>
          <span className="text-foreground/60">{" ("}</span>
          {"\n  "}
          <span className="text-foreground/60">{"<"}</span>
          <span className="text-sky-400">MoonGateProvider</span>
          <span className="text-foreground/80">{" apiKey"}</span>
          <span className="text-foreground/60">=</span>
          <span className="text-foreground">{"{API_KEY}"}</span>
          <span className="text-foreground/80">{" apiUrl"}</span>
          <span className="text-foreground/60">=</span>
          <span className="text-foreground">{"{API_URL}"}</span>
          <span className="text-foreground/60">{">"}</span>
          {"\n    "}
          <span className="text-foreground/60">{"<"}</span>
          <span className="text-sky-400">HarnessBody</span>
          <span className="text-foreground/60">{" />"}</span>
          {"\n  "}
          <span className="text-foreground/60">{"</"}</span>
          <span className="text-sky-400">MoonGateProvider</span>
          <span className="text-foreground/60">{">"}</span>
          {"\n"}
          <span className="text-foreground/60">{");"}</span>
        </code>
      </pre>
    </div>
  );
}

function AppFrame() {
  return (
    <div className="overflow-hidden rounded-2xl border border-border/60 bg-card/60 shadow-glow backdrop-blur-xl">
      <div className="flex items-center gap-2 border-b border-border/60 px-4 py-2.5">
        <span className="size-3 rounded-full bg-rose-400/70" />
        <span className="size-3 rounded-full bg-amber-400/70" />
        <span className="size-3 rounded-full bg-emerald-400/70" />
        <div className="ml-2 flex-1 rounded-md bg-muted/60 px-3 py-1 text-center text-xs text-muted-foreground">
          yourapp.com
        </div>
      </div>
      <div className="relative grid min-h-[30rem] place-items-center p-6">
        <WalletAdapter />
      </div>
    </div>
  );
}

export function Waas() {
  return (
    <section id="developers" className="relative overflow-hidden px-4 py-16 sm:py-20">
      <Spotlight fill="rgba(124,58,237,0.16)" className="left-1/2 top-0 -translate-x-1/2" />

      <Reveal className="mx-auto mb-12 max-w-2xl text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
          Wallet-as-a-Service
        </p>
        <h2 className="mt-3 text-balance text-4xl font-semibold tracking-tight sm:text-5xl">
          <PopText className="block">Your app.</PopText>
          <PopText className="block" delay={0.18}>
            <GradientText>Our gate.</GradientText>
          </PopText>
        </h2>
        <p className="mx-auto mt-4 hidden max-w-xl text-lg text-muted-foreground md:block">
          Drop the moongate wallet into your product — users sign in with Google, Apple, or a
          wallet and get an instant cross-chain account. A few lines; fiat, custody, and every
          chain handled for you.
        </p>
      </Reveal>

      <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-2">
        <Reveal className="min-w-0 space-y-6">
          <CodeBlock />
          <FeatureList features={FEATURES} />
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button asChild variant="gradient">
              <Link href={CONTACT_URL}>Contact us</Link>
            </Button>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="min-w-0">
          <AppFrame />
        </Reveal>
      </div>
    </section>
  );
}
